import { CalendarDays, Clock3, MapPin, Plus, Sparkles } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { RevealCard } from '../../components/RevealCard'
import { Button, Card, EmptyState } from '../../components/ui'
import { useToast } from '../../components/ui/toastContext'
import { CARAVAN_TRIP } from '../../config/trip'
import type { AgendaEvent, AgendaEventInput } from '../../data/local/agendaRepository'
import { AgendaRevealRepository, type AgendaRevealRecord } from '../../data/local/agendaRevealRepository'
import { caravanDb, subscribeSyncRuntime, syncNow } from '../../services/syncRuntime'
import { sessionStore } from '../access/sessionStore'
import { agendaService } from './agendaService'
import { EventEditor } from './EventEditor'
import { SurpriseEditor } from './SurpriseEditor'
import { AdminMarker } from '../access/adminView'
import { useAdminView } from '../access/useAdminView'

const dayFormatter = new Intl.DateTimeFormat('fr-FR', {
  timeZone: 'Africa/Casablanca',
  weekday: 'long',
  day: 'numeric',
  month: 'long',
})
const revealRepository = new AgendaRevealRepository(caravanDb)

export function AgendaPage() {
  const navigate = useNavigate()
  const { showToast } = useToast()
  const session = sessionStore((state) => state.session)
  const isAdminView = useAdminView()
  const [events, setEvents] = useState<AgendaEvent[]>([])
  const [editing, setEditing] = useState<AgendaEvent | 'new' | null>(null)
  const [surpriseOpen, setSurpriseOpen] = useState(false)
  const [reveals, setReveals] = useState<AgendaRevealRecord[]>([])
  const refresh = useCallback(() => {
    void agendaService.list().then(setEvents)
    void revealRepository.list().then(setReveals)
  }, [])

  useEffect(() => {
    if (!session) return
    refresh()
    return subscribeSyncRuntime(refresh)
  }, [refresh, session])

  async function save(input: AgendaEventInput, current?: AgendaEvent): Promise<void> {
    if (current) await agendaService.update(current.id, input, current.version)
    else await agendaService.create(input)
    showToast({ message: current ? 'Rendez-vous modifié.' : 'Rendez-vous ajouté.' })
    refresh()
  }

  async function remove(event: AgendaEvent): Promise<void> {
    await agendaService.delete(event)
    showToast({ message: 'Rendez-vous supprimé.' })
    refresh()
  }

  const grouped = events.reduce<Map<string, AgendaEvent[]>>((days, event) => {
    const day = days.get(event.date) ?? []
    day.push(event)
    days.set(event.date, day)
    return days
  }, new Map<string, AgendaEvent[]>())

  return (
    <main className="page agenda-page">
      <header className="page-heading">
        <div>
          <p className="eyebrow">5–19 SEPTEMBRE · MAROC</p>
          <h1>Agenda</h1>
          <p>Les rendez-vous, jour après jour.</p>
        </div>
        <Button aria-label="Ajouter un événement" onClick={() => setEditing('new')}><Plus size={18} /> Ajouter</Button>
      </header>

      {isAdminView ? (
        <Card className="surprise-admin-card">
          <Sparkles size={20} aria-hidden="true" />
          <div><AdminMarker /><strong>Surprises</strong><span>Prépare une surprise à révéler plus tard.</span></div>
          <Button variant="secondary" onClick={() => setSurpriseOpen(true)}>Préparer</Button>
        </Card>
      ) : null}

      {reveals.map((reveal) => (
        <section key={reveal.id}>
          <RevealCard
            metadata={{ id: reveal.id, state: reveal.state, publicTeaser: reveal.publicTeaser, scheduledFor: reveal.scheduledFor }}
            secret={reveal.authorizedSecret}
            online={navigator.onLine}
            clockOrigin={reveal.authoritativeServerTime && reveal.snapshotReceivedAtDevice
              ? { serverTime: reveal.authoritativeServerTime, receivedAtDevice: reveal.snapshotReceivedAtDevice }
              : null}
          />
          {isAdminView && session && reveal.state !== 'revealed' ? (
            <Button disabled={reveal.revealRequested} variant="ghost" onClick={() => void (async () => {
              await revealRepository.revealNow(reveal.id, session.role)
              refresh()
              if (navigator.onLine) await syncNow()
            })()}>{reveal.revealRequested ? 'Révélation en attente' : 'Révéler maintenant'}</Button>
          ) : null}
        </section>
      ))}

      <div className="agenda-days">
        {events.length === 0 ? (
          <EmptyState
            title="Rien de prévu pour le moment"
            detail={`Tu peux ajouter un rendez-vous entre le ${CARAVAN_TRIP.startsOn.slice(8)} et le ${CARAVAN_TRIP.endsOn.slice(8)} septembre.`}
          />
        ) : null}
        {[...grouped.entries()].map(([date, dayEvents]) => (
          <section className="agenda-day" key={date}>
            <header>
              <CalendarDays size={17} aria-hidden="true" />
              <h2>{dayFormatter.format(new Date(`${date}T12:00:00Z`))}</h2>
            </header>
            <div className="agenda-event-list">
              {dayEvents.map((event) => (
                <Card className="agenda-event" key={event.id}>
                  <time><Clock3 size={14} aria-hidden="true" /> {event.time}</time>
                  <div>
                    <h3>{event.title}</h3>
                    <span><MapPin size={13} aria-hidden="true" /> {event.place.length > 0 ? event.place : 'Lieu à préciser'}</span>
                  </div>
                  <footer>
                    <Button variant="ghost" onClick={() => setEditing(event)}>Modifier</Button>
                    <Button
                      aria-label={`Ajouter ${event.title} à la carte`}
                      variant="ghost"
                      onClick={() => {
                        const atlas = /Atlas|Imlil|Tichka|Aït-Ben-Haddou/u.test(event.place)
                        const params = new URLSearchParams({
                          source: 'event', title: event.title, place: event.place,
                          lat: atlas ? '31.137' : '31.6295', lon: atlas ? '-7.918' : '-7.9811',
                        })
                        void navigate(`/carte?${params.toString()}`)
                      }}
                    ><MapPin size={15} /></Button>
                  </footer>
                </Card>
              ))}
            </div>
          </section>
        ))}
      </div>

      {editing && session ? (
        <EventEditor
          event={editing === 'new' ? undefined : editing}
          tripId={session.tripId}
          onClose={() => setEditing(null)}
          onSave={save}
          onDelete={remove}
        />
      ) : null}
      {surpriseOpen && session ? <SurpriseEditor onClose={() => setSurpriseOpen(false)} onPrepared={async (surprise) => {
        await revealRepository.prepare({
          tripId: session.tripId, publicTeaser: surprise.teaser,
          scheduledFor: surprise.scheduledFor, secret: { title: surprise.secretTitle },
        })
        refresh()
        if (navigator.onLine) await syncNow()
      }} /> : null}
    </main>
  )
}
